'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["name"];
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Set as ImmutableSet } from 'immutable';
import isEmpty from 'transmute/isEmpty';
import { RecipientType } from 'share-with-third-party-component-lib/api/model/common/RecipientType';
import { SHARE_EMAIL, SHARE_SLACK, SEND_NOW_OPTION, RECURRING_OPTION, THIRD_PARTY_APP_CHANNEL } from './shareTypes';
import { SCHEDULE_FREQUENCY, DEFAULT_SCHEDULED_HOUR } from '../../lib/schedulingLogic';
import { getEventValue } from '../../utils/jsEventUtils';
import { getUserId } from '../../data/schemas/userInfoSchema';
import useUserInfo from '../../access/useUserInfo';
import { DEFAULT_EXPORT_TYPE } from '../shared/emailLogic';
import { useShareThirdPartyApp } from '../../data/hooks/useShareThirdPartyApp';
import useExportReportProblem from '../exportReport/useExportReportProblem';
import { jsx as _jsx } from "react/jsx-runtime";
const ControlsContext = /*#__PURE__*/createContext();

// TODO (when implementing "Share with Dashboard"):
// Make sure to add-
// assetSpecificTrackingProps, validationError, renderAdditionalFields, resetSelectedReports
// (With resetSelectedReports- make sure to call it WITH & WHEN you call setShareCadence
//     // To further explain, Since DBs are subject to change, we must reset the selected.
// reports whenever a user changes the cadence option
// to ensure that an email does not get scheduled with selected emails.)
const ControlsProvider = ({
  appUsageTracker,
  assetSpecificTrackingProps,
  assetName,
  reportId,
  objectType,
  scheduledSharedAsset = {},
  report,
  children
}) => {
  const {
    exportType: prevExportType,
    deliveryType: prevDeliveryType,
    subject: prevSubject,
    id: scheduledEmailId = null,
    message: prevMessage,
    nickname: prevNickname,
    ownerId: prevOwnerId,
    recipientUserIds: prevRecipientUserIds,
    scheduleFrequency: prevScheduledFrequency,
    scheduledOnDays: prevScheduledOnDays,
    scheduledAtHour: prevScheduledAtHour = null,
    isUtc = false,
    recipientDestination: prevRecipientDestination
  } = scheduledSharedAsset;
  const {
    data: userInfo
  } = useUserInfo();
  const currentUserId = getUserId(userInfo);
  const [thirdPartyAppShareOptionSelection, setThirdPartyAppShareOptionSelection] = useState(prevRecipientDestination && JSON.parse(prevRecipientDestination) || {});
  const [currentDialog, setCurrentDialog] = useState(prevDeliveryType === SHARE_EMAIL ? SHARE_EMAIL : thirdPartyAppShareOptionSelection.appId || SHARE_EMAIL);
  const ownerId = prevOwnerId || currentUserId;
  const isEditingScheduledEmail = Object.keys(scheduledSharedAsset).length > 0;
  const [emailSubject, setEmailSubject] = useState(prevSubject || assetName);
  const [externalValidationError, setExternalValidationError] = useState(false);
  const [formattedExternalRecipientSet, setFormattedExternalRecipientSet] = useState([]);
  const [dataExportType, setDataExportType] = useState(prevExportType || DEFAULT_EXPORT_TYPE.NONE);
  const [message, setMessage] = useState(prevMessage || '');
  const [nickname, setNickname] = useState(prevNickname || '');
  const [shareCadence, setShareCadence] = useState(isEditingScheduledEmail ? RECURRING_OPTION : SEND_NOW_OPTION);
  const [recipientSet, setRecipientSet] = useState(ImmutableSet(prevRecipientUserIds));
  const {
    data: thirdPartyApp,
    loading: thirdPartyAppLoading
  } = useShareThirdPartyApp(currentDialog);
  const isThirdPartyApp = Boolean(thirdPartyApp);
  const prevThirdPartyAppShareOptionType = isThirdPartyApp && isEditingScheduledEmail && RecipientType[thirdPartyAppShareOptionSelection.type];
  const [thirdPartyAppShareOptionType, setThirdPartyAppShareOptionType] = useState(prevThirdPartyAppShareOptionType || THIRD_PARTY_APP_CHANNEL);
  const [shareScheduleFrequency, setShareScheduleFrequency] = useState(prevScheduledFrequency || SCHEDULE_FREQUENCY.DAILY);
  const [shareScheduleAtHour, setShareScheduleAtHour] = useState(prevScheduledAtHour === null ? DEFAULT_SCHEDULED_HOUR : prevScheduledAtHour);
  const [shareScheduleOnDays, setShareScheduleOnDay] = useState(prevScheduledOnDays || []);
  const isRecurringShareAsset = shareCadence === RECURRING_OPTION;
  const dataExportProblem = useExportReportProblem({
    report,
    hasLimitedReportTypeSupport: true
  });
  useEffect(() => {
    if (!prevRecipientUserIds || prevRecipientUserIds.length === 0) {
      setRecipientSet(ImmutableSet([currentUserId]));
    }
  }, [currentUserId, prevRecipientUserIds]);
  const disableSubmit = () => {
    const hasRecipientError = recipientSet.isEmpty() && !formattedExternalRecipientSet.size;
    const hasSchedulerError = (shareScheduleFrequency === SCHEDULE_FREQUENCY.WEEKLY || shareScheduleFrequency === SCHEDULE_FREQUENCY.MONTHLY) && isEmpty(shareScheduleOnDays);
    // TODO: Share with Dashboard - add validationError
    if (externalValidationError || hasRecipientError || hasSchedulerError) return true;
    if (isRecurringShareAsset) {
      if (!nickname || currentDialog === SHARE_EMAIL && recipientSet.isEmpty()) return true;
    }
    return isThirdPartyApp && (isEmpty(thirdPartyAppShareOptionSelection) || isEmpty(thirdPartyAppShareOptionSelection.id));
  };
  const onChangeRecipientSet = useCallback(event => {
    const newSelection = ImmutableSet(getEventValue(event));
    setRecipientSet(newSelection);
  }, []);
  const commonPayload = {
    subject: emailSubject,
    message,
    exportType: dataExportType,
    reportsPerPage: 1,
    recipientUserIds: recipientSet.toArray(),
    deliveryType: thirdPartyApp ? SHARE_SLACK : SHARE_EMAIL
    // ...assetSpecificPayload, // TODO: Share with Dashboard
  };
  const additionalPayload = isRecurringShareAsset ? {
    nickname,
    ownerId,
    scheduleFrequency: shareScheduleFrequency,
    scheduledOnDays: shareScheduleOnDays,
    scheduledAtHour: shareScheduleAtHour,
    isUtc
  } : {
    externalRecipientEmails: Array.from(formattedExternalRecipientSet)
  };
  const getThirdPartyAppRecipientData = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _thirdPartyAppShareOp = Object.assign({}, thirdPartyAppShareOptionSelection),
      recipientCopy = _objectWithoutPropertiesLoose(_thirdPartyAppShareOp, _excluded);
    return JSON.stringify(recipientCopy);
  };
  const thirdPartyAppPayload = isThirdPartyApp && {
    recipientDestination: getThirdPartyAppRecipientData()
  };
  const editAssetPayload = isEditingScheduledEmail && {
    scheduledEmailId
  };
  const payload = Object.assign({}, commonPayload, additionalPayload, thirdPartyAppPayload, editAssetPayload);
  const contextValue = {
    report,
    currentDialog,
    setCurrentDialog,
    shareCadence,
    setShareCadence,
    emailSubject,
    setEmailSubject,
    recipientSet,
    onChangeRecipientSet,
    dataExportType,
    setDataExportType,
    formattedExternalRecipientSet,
    setFormattedExternalRecipientSet,
    externalValidationError,
    setExternalValidationError,
    message,
    setMessage,
    nickname,
    setNickname,
    thirdPartyAppShareOptionType,
    setThirdPartyAppShareOptionType,
    thirdPartyAppShareOptionSelection,
    setThirdPartyAppShareOptionSelection,
    shareScheduleFrequency,
    setShareScheduleFrequency,
    shareScheduleAtHour,
    setShareScheduleAtHour,
    shareScheduleOnDays,
    setShareScheduleOnDay,
    // read-only variables
    thirdPartyApp,
    thirdPartyAppLoading,
    disableSubmit,
    appUsageTracker,
    assetSpecificTrackingProps,
    isEditingScheduledEmail,
    isRecurringShareAsset,
    objectType,
    reportId,
    isUtc,
    payload,
    dataExportProblem,
    isThirdPartyApp
  };
  return /*#__PURE__*/_jsx(ControlsContext.Provider, {
    value: contextValue,
    children: children
  });
};
export const useControlsContext = () => {
  const controlsState = useContext(ControlsContext);
  if (controlsState === undefined) {
    throw new Error('Expected `ControlsContext` to have a non-nil value! This component must be rendered within a `ControlsContext.Provider`!');
  }
  return controlsState;
};
export default ControlsProvider;